import { invalidateConfigQueries } from "@/lib/config-pagination";
import { Fragment, useEffect, useRef, useState } from "react";
import { List, Loader2, Network, RefreshCw, Save } from "lucide-react";
import { useMutationState, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import type { ConfigNode } from "@/api/config-server-stages";
import { ConfigServerAlert } from "@/components/agents/configuration-server/ConfigServerAlert";
import {
  ActivityManagementTab,
  type NodeCountsView,
} from "@/components/agents/configuration-server/stages/ActivityManagementTab";
import { ManagementDraftProvider } from "@/components/agents/configuration-server/stages/ManagementDraft";
import { useManagementDraft } from "@/components/agents/configuration-server/stages/managementDraftContext";
import { OrgManagementTab } from "@/components/agents/configuration-server/stages/OrgManagementTab";
import { StageAlert } from "@/components/agents/configuration-server/stages/StageAlert";
import {
  OrgStructureSyncAction,
  OrgStructureSyncAlert,
} from "@/components/agents/configuration-server/stages/OrgStructureSyncAction";
import {
  LeadNumber,
  StageFooter,
  StageHeader,
  StageNavButtons,
} from "@/components/agents/configuration-server/stages/stageNav";
import { stageStep } from "@/components/agents/configuration-server/stages/stageOrder";
import { UnsavedChangesGuard } from "@/components/agents/configuration-server/stages/UnsavedChangesGuard";
import { StageLoading } from "@/components/agents/configuration-server/stages/StageLoading";
import {
  UploadStatusBanner,
  type UploadStatusTone,
} from "@/components/admin/reference-data/UploadStatusBanner";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  extractionRefreshFlags,
  useConfigServerOverview,
  useExtractionRefresh,
  useExtractionArtifact,
  useStartExtractionRefresh,
} from "@/hooks/useConfigServerStages";
import type { DraftNode } from "@/lib/management-draft";
import { useActivityCounts } from "@/hooks/useManagementLists";
import { errMessage } from "@/lib/reference-data-edit";

import { ExtractionProgressBar } from "./ExtractionProgressBar";

const TABS: { node: ConfigNode; label: string; subtitle: string }[] = [
  {
    node: "org_structure",
    label: "Organizational structure",
    subtitle: "Hierarchy as configured today",
  },
  {
    node: "sscui",
    label: "SSCUI configurations",
    subtitle: "Application area · Scope item · Activity",
  },
  {
    node: "module_config",
    label: "App Fiori configurations",
    subtitle: "Configuration",
  },
];

const TAB_TRIGGER_CLASS =
  "group gap-2 rounded-b-none rounded-t-md border-b-2 border-transparent px-4 py-3 font-semibold text-white/75 shadow-none data-[state=active]:border-[var(--exed-gold)] data-[state=active]:bg-[var(--exed-surface)] data-[state=active]:text-[var(--exed-ink-900)] data-[state=active]:shadow-none";
const TAB_PILL_CLASS =
  "rounded-full bg-white/[0.12] px-2 py-px text-xs font-semibold text-white group-data-[state=active]:bg-[rgba(255,200,61,0.25)] group-data-[state=active]:text-[#7a5600]";

export default function ManagementStage() {
  const { projectId } = useParams<{ projectId: string }>();
  const overviewQuery = useConfigServerOverview(projectId);
  const overview = overviewQuery.data;
  const activeExtraction = overview?.active_run?.kind === "extraction";
  const summary = useExtractionArtifact(
    projectId,
    activeExtraction,
    overview?.stages.management.state !== "locked",
  );
  const refetchSummary = summary.refetch;
  const observedExtraction = useRef<{ projectId: string; id: string } | null>(
    null,
  );
  useEffect(() => {
    if (projectId && overview?.active_run?.kind === "extraction") {
      observedExtraction.current = { projectId, id: overview.active_run.id };
    } else if (
      overview &&
      observedExtraction.current?.projectId === projectId
    ) {
      observedExtraction.current = null;
      void refetchSummary();
    } else if (observedExtraction.current?.projectId !== projectId) {
      observedExtraction.current = null;
    }
  }, [projectId, overview, refetchSummary]);
  const artifactId =
    summary.data?.artifact_status === "ready"
      ? (summary.data.artifact_id ?? null)
      : null;

  if (!projectId) return null;

  // Um rascunho por projeto e extração: trocar de projeto começa do zero.
  return (
    <ManagementDraftProvider
      key={`${projectId}:${artifactId ?? ""}`}
      projectId={projectId}
      artifactId={artifactId}
    >
      <ManagementScreen
        projectId={projectId}
        overview={overview}
        overviewError={overviewQuery.isError}
        refetchOverview={() => void overviewQuery.refetch()}
        summary={summary}
        artifactId={artifactId}
      />
    </ManagementDraftProvider>
  );
}

function ManagementScreen({
  projectId,
  overview,
  overviewError: isError,
  refetchOverview: refetch,
  summary,
  artifactId,
}: {
  projectId: string;
  overview: ReturnType<typeof useConfigServerOverview>["data"];
  overviewError: boolean;
  refetchOverview: () => void;
  summary: ReturnType<typeof useExtractionArtifact>;
  artifactId: string | null;
}) {
  const { t, i18n } = useTranslation();
  const draft = useManagementDraft();
  const activityCounts = useActivityCounts(projectId);
  const mutationStates = useMutationState({
    filters: { mutationKey: ["config-server", "scope-verdicts", projectId] },
    select: (mutation) => mutation.state.status,
  });
  const saving = mutationStates.includes("pending");
  const lastStatus = mutationStates.at(-1);
  const [tab, setTab] = useState<ConfigNode>("org_structure");
  const [view, setView] = useState<"list" | "map">("list");
  const centralHref = `/projects/${projectId}/agents/configuration-server`;
  const management = overview?.stages.management;
  const queryClient = useQueryClient();
  const activeExtraction = overview?.active_run?.kind === "extraction";
  const refreshQuery = useExtractionRefresh(projectId, {
    watch: overview?.extraction_refresh?.running === true,
  });
  const startRefresh = useStartExtractionRefresh(projectId);
  const refreshFlags = extractionRefreshFlags(overview, refreshQuery.data);
  const refreshing = refreshFlags.running;
  const refreshProgress = refreshQuery.data?.progress ?? null;
  // O botão do cabeçalho vale para qualquer aba; o alerta acima da lista só aparece nas abas do catálogo.
  const showRefresh =
    (refreshFlags.available || refreshing) && !activeExtraction;
  const showRefreshAlert =
    showRefresh && !refreshing && (tab === "sscui" || tab === "module_config");

  const extractLatest = () =>
    startRefresh.mutate(undefined, {
      onError: (err) =>
        toast.error(errMessage(err, t("The update could not be extracted."))),
    });

  // Fim da atualização: avisa o resultado e relê o que a tela mostra.
  const previousStatus = useRef<string | undefined>(undefined);
  // O resultado fica na tela (faixa acima do conteúdo) até o próximo clique; o toast sozinho some rápido.
  const [refreshOutcome, setRefreshOutcome] = useState<{
    tone: UploadStatusTone;
    message: string;
    details?: string[];
  } | null>(null);
  useEffect(() => {
    const status = refreshQuery.data?.refresh?.status;
    if (status === "running") setRefreshOutcome(null);
    if (
      previousStatus.current === "running" &&
      status &&
      status !== "running"
    ) {
      const result = refreshQuery.data!.refresh!;
      const warnings = result.warnings ?? [];
      if (status === "done") {
        const records = result.records_read;
        const message = result.message
          ? t("Nothing new to extract")
          : typeof records === "number"
            ? t(
                "{{tables}} table(s), {{records}} row(s) re-read, {{fields}} new field(s)",
                {
                  tables: result.tables_reread ?? 0,
                  records: records.toLocaleString(i18n.language),
                  fields: result.fields_added ?? 0,
                },
              )
            : t("{{tables}} table(s) re-read, {{fields}} new field(s)", {
                tables: result.tables_reread ?? 0,
                fields: result.fields_added ?? 0,
              });
        toast.success(message);
        setRefreshOutcome({ tone: "success", message, details: warnings });
      } else {
        const message =
          result.errors?.[0] ?? t("The update could not be extracted.");
        toast.error(message);
        setRefreshOutcome({ tone: "error", message });
      }
      void invalidateConfigQueries(queryClient, ["config-server"]);
    }
    previousStatus.current = status;
  }, [refreshQuery.data, queryClient, t, i18n.language]);

  const ready =
    !!management &&
    management.state !== "locked" &&
    !isError &&
    !summary.isError &&
    !!summary.data &&
    !!artifactId;
  const tabIndex = TABS.findIndex((entry) => entry.node === tab);
  const current = TABS[tabIndex];
  // Os números da tela seguem o rascunho; a central e o % geral só mudam ao salvar.
  const changes = draft?.changes ?? 0;
  const pendingDelta = (node: DraftNode) => draft?.deltas[node].pending ?? 0;
  const savedCounts = (node: ConfigNode) =>
    node === "org_structure"
      ? management?.by_node[node]
      : (activityCounts.data?.by_node[node] ?? management?.by_node[node]);
  const pendingOf = (node: ConfigNode) =>
    Math.max(0, (savedCounts(node)?.pending ?? 0) + pendingDelta(node));
  const pendingTotal = Math.max(
    0,
    (management?.pending ?? 0) +
      TABS.reduce((sum, entry) => sum + pendingDelta(entry.node), 0),
  );
  const decided = management
    ? Math.min(management.total, Math.max(0, management.total - pendingTotal))
    : 0;
  const countsOf = (node: DraftNode): NodeCountsView => ({
    pending: pendingOf(node),
    approved: Math.max(
      0,
      (savedCounts(node)?.approved ?? 0) + (draft?.deltas[node].approved ?? 0),
    ),
    out_of_scope: Math.max(
      0,
      (savedCounts(node)?.out_of_scope ?? 0) +
        (draft?.deltas[node].out_of_scope ?? 0),
    ),
  });
  // A Execução destrava com pelo menos uma aprovada salva, em qualquer aba (ou com
  // tudo decidido); quem decide é o servidor (spec 2026-10-26, D16).
  const executionLocked =
    !overview || overview.stages.execution.state === "locked";
  const approvedTotal = TABS.reduce(
    (sum, entry) => sum + countsOf(entry.node).approved,
    0,
  );
  const unlocksOnSave =
    changes > 0 && (approvedTotal > 0 || pendingTotal === 0);
  const decidedPercent =
    management && management.total > 0
      ? Math.round((decided / management.total) * 100)
      : 0;

  const retryFailedReads = () => {
    if (isError) refetch();
    if (summary.isError) void summary.refetch();
  };

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setTab(value as ConfigNode)}
      className="relative -m-6 flex min-h-[calc(100%+3rem)] flex-col"
    >
      {draft && (
        <UnsavedChangesGuard
          changes={changes}
          saving={draft.saving}
          onSave={draft.save}
          onDiscard={draft.discard}
        />
      )}
      <StageHeader
        number="02"
        title={t("Management")}
        centralHref={centralHref}
        actions={
          <>
            {/* O Diagnóstico (a frase e o botão) à esquerda; a extração da atualização à direita. */}
            {ready ? <OrgStructureSyncAction projectId={projectId} /> : null}
            {showRefresh && (
              <div className="flex flex-col items-end gap-2">
                <Button
                  variant="action"
                  className="gap-1.5"
                  disabled={refreshing || startRefresh.isPending}
                  onClick={extractLatest}
                >
                  {refreshing ? (
                    <Loader2
                      aria-hidden="true"
                      className="h-4 w-4 animate-spin"
                    />
                  ) : (
                    <RefreshCw aria-hidden="true" className="h-4 w-4" />
                  )}
                  {refreshing
                    ? t("Extracting latest update…")
                    : t("Extract latest update")}
                </Button>
                {refreshing && (
                  <ExtractionProgressBar
                    progress={refreshProgress}
                    label={t("Extracting latest update…")}
                    tone="dark"
                    className="w-[320px]"
                  />
                )}
              </div>
            )}
          </>
        }
        tabs={
          ready && (
            <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none bg-transparent p-0 text-white">
              {TABS.map(({ node, label }) => (
                <TabsTrigger
                  key={node}
                  value={node}
                  className={TAB_TRIGGER_CLASS}
                >
                  <span>{t(label)}</span>
                  <span className={TAB_PILL_CLASS}>
                    {pendingOf(node).toLocaleString("en-US")}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          )
        }
      >
        {management && management.state !== "locked" && (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <p className="text-sm text-white/70">
              <LeadNumber
                text={t("{{decided}} of {{total}} decided", {
                  decided: decided.toLocaleString("en-US"),
                  total: management.total.toLocaleString("en-US"),
                })}
                lead={decided.toLocaleString("en-US")}
                strongClassName="text-white"
              />
              {saving ? (
                <span>
                  <span aria-hidden="true"> · </span>
                  <span>{t("Saving…")}</span>
                </span>
              ) : lastStatus === "success" && changes === 0 ? (
                <span>
                  <span aria-hidden="true"> · </span>
                  <span>{t("Saved ✓")}</span>
                </span>
              ) : null}
            </p>
            <div
              role="progressbar"
              aria-label={t("Decided")}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={decidedPercent}
              className="h-1.5 w-[200px] overflow-hidden rounded-full bg-white/[0.12]"
            >
              <div
                className="h-full rounded-full bg-[var(--exed-gold)]"
                style={{ width: `${Math.max(decidedPercent, 0.5)}%` }}
              />
            </div>
          </div>
        )}
      </StageHeader>

      <div className="mx-auto w-full max-w-[1488px] flex-1 space-y-4 px-6 pb-6 pt-5">
        {refreshing && (
          <div
            data-tone="progress"
            className="rounded-xl border border-[var(--exed-card-border)] bg-white px-4 py-3"
          >
            <ExtractionProgressBar
              progress={refreshProgress}
              label={t("Extracting latest update…")}
            />
          </div>
        )}
        {!refreshing && refreshOutcome && (
          <UploadStatusBanner
            tone={refreshOutcome.tone}
            message={refreshOutcome.message}
            details={refreshOutcome.details}
            onDismiss={() => setRefreshOutcome(null)}
          />
        )}
        {isError || summary.isError ? (
          <ConfigServerAlert
            message={t("Could not load this page's data.")}
            onRetry={retryFailedReads}
          />
        ) : !management ? (
          <StageLoading variant="page" />
        ) : management.state === "locked" ? (
          <div className="rounded-xl border border-[var(--exed-card-border)] bg-white p-6 text-sm">
            <p>{t("Unlocks when the extraction finishes")}</p>
            <Link
              to={centralHref}
              className="mt-2 inline-block font-semibold text-[#1d4ea0] underline"
            >
              {t("Configuration Server")}
            </Link>
          </div>
        ) : !summary.data ? (
          <StageLoading variant="page" />
        ) : !artifactId ? (
          <div>
            <p>{t("Unlocks when the extraction finishes")}</p>
            <Link to={centralHref}>{t("Configuration Server")}</Link>
          </div>
        ) : (
          <Fragment key={`${projectId}:${artifactId}`}>
            {tab === "org_structure" && (
              <OrgStructureSyncAlert projectId={projectId} />
            )}
            {showRefreshAlert && (
              <StageAlert
                message={t(
                  "A newer upload is available. Extract the latest update to bring it here.",
                )}
                action={{
                  label: (
                    <>
                      <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
                      {t("Click here to extract")}
                    </>
                  ),
                  onClick: extractLatest,
                  disabled: startRefresh.isPending,
                }}
              />
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                {/* "Lido do seu ERP" só vale na estrutura: SSCUI e App Fiori vêm do catálogo × escopo aprovado. */}
                {tab === "org_structure" && (
                  <span className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--exed-ink-500)]">
                    {t("READ FROM YOUR ERP")}
                  </span>
                )}
                <span className="text-base font-semibold text-[var(--exed-ink-900)]">
                  {t(current.subtitle)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {/* Lista/Mapa é só da estrutura: SSCUI e App Fiori são listas (a SSCUI agrupa por área e scope item, a App Fiori é plana). */}
                {tab === "org_structure" && (
                  <ToggleGroup
                    type="single"
                    aria-label={t("View")}
                    value={view}
                    onValueChange={(value) => {
                      if (value === "list" || value === "map") setView(value);
                    }}
                    className="gap-0.5 rounded-lg border border-[var(--exed-card-border)] bg-white p-[3px]"
                  >
                    <ToggleGroupItem
                      value="list"
                      className="h-[30px] gap-1.5 rounded-md px-3 text-[13px] font-medium text-[var(--exed-ink-500)] data-[state=on]:bg-[var(--exed-navy)] data-[state=on]:text-white"
                    >
                      <List aria-hidden="true" className="h-3.5 w-3.5" />
                      {t("List")}
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="map"
                      className="h-[30px] gap-1.5 rounded-md px-3 text-[13px] font-medium text-[var(--exed-ink-500)] data-[state=on]:bg-[var(--exed-navy)] data-[state=on]:text-white"
                    >
                      <Network aria-hidden="true" className="h-3.5 w-3.5" />
                      {t("Map")}
                    </ToggleGroupItem>
                  </ToggleGroup>
                )}
                {/* O rascunho das três abas grava de uma vez. */}
                {draft && changes > 0 && (
                  <Button
                    variant="action"
                    className="h-9 gap-1.5"
                    disabled={draft.saving}
                    onClick={() => void draft.save()}
                  >
                    <Save aria-hidden="true" className="h-4 w-4" />
                    {draft.saving
                      ? t("Saving…")
                      : t("Save ({{changes}})", {
                          changes: changes.toLocaleString("en-US"),
                        })}
                  </Button>
                )}
              </div>
            </div>
            <TabsContent value="org_structure" className="mt-0">
              <OrgManagementTab
                projectId={projectId}
                view={view}
                counts={countsOf("org_structure")}
                artifactId={artifactId}
              />
            </TabsContent>
            <TabsContent value="sscui" className="mt-0">
              <ActivityManagementTab
                projectId={projectId}
                node="sscui"
                counts={countsOf("sscui")}
                artifactId={artifactId}
              />
            </TabsContent>
            <TabsContent value="module_config" className="mt-0">
              <ActivityManagementTab
                projectId={projectId}
                node="module_config"
                counts={countsOf("module_config")}
                artifactId={artifactId}
              />
            </TabsContent>
          </Fragment>
        )}
      </div>

      {ready && (
        <StageFooter>
          <div className="flex flex-col gap-1 text-[13px] text-[var(--exed-ink-500)]">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                <LeadNumber
                  text={t("{{pending}} pending in {{tab}}", {
                    pending: pendingOf(tab).toLocaleString("en-US"),
                    tab: t(current.label),
                  })}
                  lead={pendingOf(tab).toLocaleString("en-US")}
                  strongClassName="text-[var(--exed-ink-900)]"
                />
              </span>
              <span aria-hidden="true" className="text-[var(--exed-ink-400)]">
                ·
              </span>
              <span>
                {t("Step {{step}} of 3", { step: stageStep("management") })}
              </span>
            </div>
            {executionLocked &&
              tab === "org_structure" &&
              pendingOf("org_structure") > 0 && (
                <span>
                  {t(
                    "Organizational units from the Diagnostic are decided there, then synced here.",
                  )}
                </span>
              )}
          </div>
          {/* Os botões trocam de estágio (Extração, Gerenciamento, Execução), não de aba: as abas do topo
              já permitem a navegação entre as listas. Só a passagem para a Execução trava, com o
              motivo à vista, como na etapa 1. */}
          <StageNavButtons
            stage="management"
            centralHref={centralHref}
            nextReason={
              executionLocked
                ? unlocksOnSave
                  ? t("Save your changes to unlock Execution.")
                  : t("Approve at least one item to unlock Execution.")
                : undefined
            }
          />
        </StageFooter>
      )}
    </Tabs>
  );
}
